// Дубль пары (клиент, центр затрат) в составе клиентов претензии нарушает уникальный индекс
// pretension_client_uindex (pretension_id, client_id, cost_center_id): сборка падает на вставке
// дочерней записи, и jBPM уводит претензию в статус problem, откуда её нельзя ни открыть, ни
// отменить, ни вернуть в черновик. Один клиент в НЕСКОЛЬКИХ РАЗНЫХ центрах затрат — норма
// (решение заказчика от 11.09.2026): запрещаем только полный дубль пары.

// Сообщение под полем «Центр затрат» задвоенной строки — парное к noDuplicateClientCostCenter.
export const DUPLICATE_CLIENT_COST_CENTER_ERROR = 'Клиент с этим центром затрат уже указан';

// Хвост тоста: что пользователю с этим делать (схлопывать строки за него нельзя — суммы его).
const DUPLICATE_PAIRS_HINT = 'Объедините строки или измените центр затрат.';

interface ClientLike {
  id?: string | number | null;
  showing_name?: string | null;
  legal_name?: string | null;
  name?: string | null;
}

export interface CashAccountRow {
  client?: ClientLike | null;
  costCenter?: { id?: string | number | null; name?: string | null } | null;
}

export interface DuplicateCashAccountPair {
  clientName: string;
  costCenterName: string;
  count: number;
}

// Ключ пары — по идентификаторам: справочник отдаёт один и тот же объект разными экземплярами
// (сравнение по ссылке дало бы ложный пропуск), а названия центров затрат не уникальны.
// Незаполненная строка ключа не имеет: за client/costCenter отвечает required, и две пустые
// строки дублем не считаются.
const pairKey = (row: CashAccountRow): string | null => {
  const clientId = row?.client?.id;
  const costCenterId = row?.costCenter?.id;
  if (clientId === null || clientId === undefined || costCenterId === null || costCenterId === undefined) {
    return null;
  }
  return `${clientId}|${costCenterId}`;
};

// Имя клиента как в дропдауне (NormalizerMixin: showing_name → legal_name); id — последний
// рубеж, чтобы тост не превратился в «Клиент «» с центром затрат «»».
const clientLabel = (client?: ClientLike | null): string => String(
  client?.showing_name || client?.legal_name || client?.name || client?.id || '',
);

// Задвоенные пары в порядке первого появления — по одной записи на пару, со счётчиком строк.
export const findDuplicateCashAccountPairs = (
  rows: CashAccountRow[] | null | undefined,
): DuplicateCashAccountPair[] => {
  const seen = new Map<string, DuplicateCashAccountPair>();
  (rows || []).forEach((row) => {
    const key = pairKey(row);
    if (key === null) return;
    const known = seen.get(key);
    if (known) {
      known.count += 1;
      return;
    }
    seen.set(key, {
      clientName: clientLabel(row.client),
      costCenterName: String(row.costCenter?.name ?? ''),
      count: 1,
    });
  });
  return Array.from(seen.values()).filter((pair) => pair.count > 1);
};

// Индексы строк, входящих в задвоенную пару — для подсветки конкретных строк формы.
export const duplicateCashAccountRowIndexes = (
  rows: CashAccountRow[] | null | undefined,
): number[] => {
  const counts = new Map<string, number>();
  (rows || []).forEach((row) => {
    const key = pairKey(row);
    if (key === null) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const indexes: number[] = [];
  (rows || []).forEach((row, index) => {
    const key = pairKey(row);
    if (key !== null && (counts.get(key) || 0) > 1) {
      indexes.push(index);
    }
  });
  return indexes;
};

// Vuelidate-валидатор уровня массива cashAccounts: держит $v.$invalid, через который закрыты
// предпросмотр и отправка.
export const noDuplicateClientCostCenter = (
  rows: CashAccountRow[] | null | undefined,
): boolean => findDuplicateCashAccountPairs(rows).length === 0;

// Текст тоста: по строке на задвоенную пару плюс подсказка. Пусто, если дублей нет — общее
// «Не все поля заполнены корректно» не объясняет, какая именно пара задвоена.
export const duplicateCashAccountPairsMessage = (
  rows: CashAccountRow[] | null | undefined,
): string => {
  const duplicates = findDuplicateCashAccountPairs(rows);
  if (duplicates.length === 0) return '';
  // «несколько раз», а не счётчик: пара может повторяться и трижды, а число повторов
  // пользователю ничего не даёт — строки он всё равно ищет по названиям клиента и центра затрат.
  const lines = duplicates.map(
    (pair) => `Клиент «${pair.clientName}» с центром затрат «${pair.costCenterName}» указан несколько раз.`,
  );
  return [...lines, DUPLICATE_PAIRS_HINT].join('\n');
};
